/**
 * Client mirror of the server's retention window.
 *
 * Mirrors `apps/api/src/convoai_api/services/retention_window.py`. The server
 * clamps regardless of what we send — these helpers exist so a picker never
 * *offers* a date the backend would shorten or empty out, and so every surface
 * that renders a retained window agrees about where the boundary sits.
 *
 * This lives in one module for the same reason the server's does: the floor is
 * referenced from the dashboard range controls and the scenario window picker,
 * and those must not drift apart.
 */

/** Mirrors ANALYTICS_MAX_LOOKBACK_DAYS. */
export const RETENTION_LOOKBACK_DAYS = 365;

/** Earliest NZ day the app can report on: `[floor, today]` spans exactly
 * RETENTION_LOOKBACK_DAYS days, with today counting as day 1. */
export function retentionFloorIso(todayIso: string): string {
  const d = new Date(`${todayIso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1 - RETENTION_LOOKBACK_DAYS);
  return d.toISOString().slice(0, 10);
}

/** A custom window's start as the server will actually apply it.
 *
 * The picker can't offer a start before the floor, but a saved URL or deep
 * link still can, and the server clamps rather than rejects it. Every surface
 * that *shows* a custom start has to clamp too, or the UI advertises a window
 * wider than the data behind it. */
export function clampCustomStart(start: string, floorIso: string): string {
  return start < floorIso ? floorIso : start;
}

/** True when a custom window ends before the floor.
 *
 * Such a window has no overlap with retained data at all — there is no
 * effective window to clamp *to*, so surfaces that would render it fall back
 * to their placeholder instead of advertising an inverted range like
 * "2025-07-31 – 2020-06-01" (clamped start, ancient end). The server rejects
 * the request outright for the same reason. */
export function customRangeExpired(end: string, floorIso: string): boolean {
  return end < floorIso;
}

/** Shift an ISO day by whole days, staying on UTC so no DST offset applies. */
export function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
